import { DateTime } from 'luxon';
import axios from 'smg/axios';
import * as models from 'smg/customer-api/models';
import { GetProofURLs } from 'smg/proofs';
import QueryString from 'smg/querystring';
import * as template from 'smg/template';
import { AccountDetailsInit, PageLoader, RequireSession } from '../../account';
import { TicketExpectedBackLine, TicketNeedByLine, TicketStatus, TicketTypeLabel } from '../../design-ticket-status';
import { Relref } from '../../hugo';
import { GetFinishedQuoteLink } from '../../quote';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await RequireSession()) {
        return;
    }

    await AccountDetailsInit();

    const $container = document.querySelector('#design-tickets-container') as HTMLElement;
    const $viewMoreBtnTemplate = template.Clone('#view-more-btn-template').firstElementChild as HTMLElement;
    const $viewMoreBtn = $viewMoreBtnTemplate?.querySelector('#view-more-btn') as HTMLButtonElement;
    const $currMax = $viewMoreBtnTemplate?.querySelector('#current-max') as HTMLElement;
    const $totalTicketCount = $viewMoreBtnTemplate?.querySelector('#total-ticket-count') as HTMLElement;
    let nextPage = true;
    let emptyResults = false;
    const params: Record<string, string | number> = {};
    QueryString().delete('p');

    void axios.get('/quote-request-project-tasks', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => {
        const data = models.ColumnStatsResponse.fromJSON(resp.data);
        $totalTicketCount.textContent = String(data.count);
    });

    const pl: PageLoader<models.QuoteRequestProjectTask> = new PageLoader<models.QuoteRequestProjectTask>({
        clearResults: false,
        parent: $container,
        nextBtn: $viewMoreBtn,
        beforeLoad: () => {
            if (pl.currentPage() === 0) {
                $container.innerHTML = '';
                emptyResults = false;
                nextPage = true;
            }
        },
        get: () => axios.get('/quote-request-project-tasks', {
            params: {
                'sort': pl.sort() || 'created desc',
                'max': params?.['max'] || 5,
                'data': 'id,key,created,quoteRequestProjectTaskStatusID,quoteRequestProjectTaskTypeID,quoteRequest,quoteProof,assignedUser',
                'p': pl.currentPage(),
            },
        }).then(resp => {
            const data = models.QuoteRequestProjectTasksHyejooResponse.fromJSON(resp.data);
            if (data.quoteRequestProjectTasks.length === 0) {
                emptyResults = true;
            }
            if (!data.nextPage) {
                nextPage = false;
            } else {
                nextPage = true;
                $viewMoreBtnTemplate?.classList.remove('d-none');
            }
            return { rows: data.quoteRequestProjectTasks, nextPage: data.nextPage };
        }),

        draw: task => drawTicketCard(task),
        afterLoad: () => {
            if (emptyResults) {
                drawEmptyResults($container);
                emptyResults = false;
            }

            if (nextPage) {
                $container.appendChild($viewMoreBtnTemplate);
                $viewMoreBtnTemplate.classList.remove('d-none');
            } else {
                $viewMoreBtnTemplate.classList.add('d-none');
            }

            $currMax.textContent = String((pl.currentPage() + 1) * (typeof params?.['max'] === 'number' ? params['max'] : 5));
        },
    });
    pl.load();
}

function drawTicketCard(task: models.QuoteRequestProjectTask): HTMLElement {
    const $card = template.Clone('#design-ticket-card-template').firstElementChild as HTMLElement;

    const link = `${Relref('/design-ticket')}?qrpt=${task.key}`;
    const name = task.quoteRequest?.quoteRequestProject?.name || 'Design Ticket';

    const $name = $card.querySelector('.ticket-name') as HTMLAnchorElement;
    $name.textContent = name;
    $name.href = link;

    ($card.querySelector('.ticket-type') as HTMLElement).textContent = TicketTypeLabel(task);

    $card.querySelectorAll('.ticket-date').forEach($el => {
        $el.textContent = task.created?.isValid ? task.created.toLocaleString(DateTime.DATE_MED) : '—';
    });

    const status = TicketStatus(task);
    const $status = $card.querySelector('.ticket-status') as HTMLElement;
    $status.classList.add(status.className);
    ($status.querySelector('.ticket-status-icon') as HTMLElement).textContent = status.icon;
    ($status.querySelector('.ticket-status-label') as HTMLElement).textContent = status.label;

    // Thumbnail: the finished proof when there is one, otherwise the first
    // image the customer uploaded with their request.
    let thumb = '';
    const proofFile = task.quoteProof?.proof?.file;
    if (proofFile) {
        thumb = GetProofURLs(window.cloudFront, proofFile).thumb;
    }
    if (!thumb) {
        const upload = task.quoteRequest?.uploads?.find(u => u.mime.startsWith('image/') && u.publicURL !== '');
        thumb = upload?.publicURL ?? '';
    }
    if (thumb) {
        const $img = $card.querySelector('.ticket-thumb img') as HTMLImageElement;
        $img.src = thumb;
        $img.alt = name;
        $img.classList.remove('d-none');
        ($card.querySelector('.ticket-thumb-fallback') as HTMLElement).classList.add('d-none');
    }

    ($card.querySelector('.ticket-eta') as HTMLElement).textContent = TicketExpectedBackLine(task);
    ($card.querySelector('.ticket-need-by') as HTMLElement).textContent = TicketNeedByLine(task);

    if (task.assignedUser?.firstName) {
        ($card.querySelector('.ticket-designer') as HTMLElement).textContent = `Designer: ${task.assignedUser.firstName}`;
    } else {
        ($card.querySelector('.ticket-designer') as HTMLElement).classList.add('d-none');
    }

    ($card.querySelector('.view-ticket-btn') as HTMLAnchorElement).href = link;

    const quoteLink = GetFinishedQuoteLink(task);
    if (quoteLink) {
        const $viewQuoteBtn = $card.querySelector('.view-quote-btn') as HTMLAnchorElement;
        $viewQuoteBtn.href = quoteLink;
        $viewQuoteBtn.classList.remove('d-none');
    }

    return $card;
}

function drawEmptyResults($parent: HTMLElement) {
    $parent.innerHTML = '';
    const $empty = template.Clone('#empty-results');
    $parent.appendChild($empty);
}
